import { IExpressionResolver, IExpressionResolution, ResolutionErrorKind, failedExpressionResolution, IResolutionError } from 'app/model/expressions/expression-resolution';
import { Metamodel } from 'app/model/base/model';
import { Expression } from './expression';
import { PipelineParameterResolver, LibraryVariableResolver } from 'app/model/expressions/parameter-resolvers';
import { SystemVariableResolver } from 'app/model/expressions/system-variable-resolver';
import { Pipeline } from 'app/model/pipeline/pipeline';
import { Owned } from 'app/model/base/named';
import { TriggerParameterResolver } from './trigger-parameter-resolver';

/**
 * Supports resolution of pipeline level expressions (parameters and system variables).
 */
export class PipelineExpressionResolver implements IExpressionResolver {
    private _childResolvers: IExpressionResolver[] = [
        new PipelineParameterResolver(this._entity, this._expressionResolversCallback),
        new TriggerParameterResolver(this._registeredExpressions),
        new LibraryVariableResolver(this._entity, this._expressionResolversCallback),
        new SystemVariableResolver(this._registeredExpressions)
    ];

    /**
     * Initializes a new instance of the PipelineExpressionResolver class.
     * @param _entity The entity owning the field being parameterized.
     * @param _registeredExpressions The registered expressions to search within.
     * @param _expressionResolversCallback Provides supported expression resolvers used to resolve function arguments.
     */
    public constructor(private _entity: Owned, private _registeredExpressions: Expression[], private readonly _expressionResolversCallback: () => IExpressionResolver[]) {
    }

    public isOfType(expression: string, ignoreStart: boolean = false): boolean {
        expression = expression ? expression.trim() : '';
        const expressionPrefixMatch = ignoreStart ? 'pipeline().' : '@pipeline().';
        const expressionPrefix2Match = ignoreStart ? 'pipeline()?' : '@pipeline()?';
        const expressionPrefixMatchInvalid = ignoreStart ? 'pipeline.' : '@pipeline.';
        const expressionPrefix2MatchInvalid = ignoreStart ? 'pipeline?' : '@pipeline?';

        return expression && (expression.startsWith(expressionPrefixMatch)
            || expression.startsWith(expressionPrefix2Match)
            || expression.startsWith(expressionPrefixMatchInvalid)
            || expression.startsWith(expressionPrefix2MatchInvalid));
    }

    public resolve(expression: string, ignoreStart: boolean = false): IExpressionResolution {
        let resolution: IExpressionResolution;

        for (const resolver of this._childResolvers) {
            if (resolver.isOfType(expression, ignoreStart)) {
                resolution = resolver.resolve(expression, ignoreStart);
                break;
            }
        }

        if (!resolution) {
            resolution = failedExpressionResolution(expression, { kind: ResolutionErrorKind.Unrecognized, details: expression });
        }

        return resolution;
    }

    public checkApplicability(entity: Owned): { isApplicable: boolean, error?: IResolutionError } {
        return { isApplicable: Metamodel.modelOf(entity.concept) === Pipeline.MODEL };
    }
}
